/**
 * @fileoverview The four KPI tiles of the Sales Performance screen.
 * @description Turns the summary's KPIs into the shared KPI strip: targets assigned, targets
 * achieved, the achievement percentage and the targets at risk or overdue. The sublabels state
 * what the backend counts, so a reader knows what "achieved" means.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { formatPercent } from '@/features/sales/lib/sales-performance.utils'
import KpiStrip from '@/shared/components/molecules/KpiStrip'

import type { SalesPerformanceKpis } from '@/features/sales/types/sales-performance.types'

interface PerformanceKpiStripProps {
  kpis: SalesPerformanceKpis
}

/**
 * @component
 * @param {PerformanceKpiStripProps} props - The KPIs to show.
 * @param {SalesPerformanceKpis} props.kpis - The summary's KPIs for the selected period and team.
 */
function PerformanceKpiStrip({ kpis }: PerformanceKpiStripProps) {
  const { t } = useTranslation()

  return (
    <KpiStrip
      tiles={[
        {
          label: t('sales.salesPerformance.kpi.assigned'),
          sublabel: t('sales.salesPerformance.kpi.assignedSublabel'),
          icon: 'briefcase',
          iconTone: 'navy',
          value: kpis.assigned,
          testId: 'sales-performance-kpi-assigned',
        },
        {
          label: t('sales.salesPerformance.kpi.achieved'),
          sublabel: t('sales.salesPerformance.kpi.achievedSublabel'),
          icon: 'check',
          iconTone: 'green',
          value: kpis.achieved,
          testId: 'sales-performance-kpi-achieved',
          tone: 'success',
        },
        {
          label: t('sales.salesPerformance.kpi.achievement'),
          sublabel: t('sales.salesPerformance.kpi.achievementSublabel'),
          icon: 'trend',
          iconTone: 'blue',
          value: formatPercent(kpis.achievementPercent),
          testId: 'sales-performance-kpi-achievement',
        },
        {
          label: t('sales.salesPerformance.kpi.atRisk'),
          sublabel: t('sales.salesPerformance.kpi.atRiskSublabel'),
          icon: 'hourglass',
          iconTone: 'amber',
          value: kpis.atRiskOrOverdue,
          testId: 'sales-performance-kpi-at-risk',
          tone: kpis.atRiskOrOverdue > 0 ? 'danger' : undefined,
        },
      ]}
    />
  )
}

export default PerformanceKpiStrip
